import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Order, User } from '../types';
import { getUserOrders, getUserByTelegramId } from '../services/api';
import { getTelegramUser } from '../services/telegram';
import { colors } from '../theme/themeConfig';
import { dummyOrders } from '../data/dummyOrders';
import PageHeader from '../components/layout/PageHeader';

const PAGE_TITLE = 'История заказов';

// todo: upd check everything
const OrdersHistoryPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const username = telegramUser?.username || user?.telegramUsername || 'username';

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        
        // Get Telegram user
        const tgUser = getTelegramUser();
        // Set telegram user immediately to ensure photoUrl is available
        setTelegramUser(tgUser);
        
        // Use a default userId if no Telegram user is available
        const defaultUserId = '7345174833';
        
        if (tgUser && tgUser.id) {
          try {
            // First, get user data by Telegram ID
            const userData = await getUserByTelegramId(tgUser.id);
            setUser(userData);
            
            // Then get user orders using the telegram ID directly
            // This avoids potential mismatches between user.id and telegramId
            const ordersData = await getUserOrders(tgUser.id.toString());
            setOrders(ordersData);
          } catch (error) {
            console.error('API error, using dummy data:', error);
            // Use dummy data if API fails
            setUser({
              id: 'mock-user',
              telegramId: parseInt(defaultUserId),
              bonusBalance: 0,
              telegramUsername: tgUser.username || 'test_user',
              isBlocked: false,
              referralCode: ''
            } as User);
            setOrders(dummyOrders);
          }
        } else {
          // Use a default userId for API call
          try {
            const ordersData = await getUserOrders(defaultUserId);
            setOrders(ordersData);
          } catch (error) {
            console.error('Error loading orders with default userId:', error);
            setOrders(dummyOrders);
          }
          
          // Set a default user if we don't have one
          if (!user) {
            setUser({
              id: defaultUserId,
              telegramId: parseInt(defaultUserId),
              telegramUsername: username,
              bonusBalance: 0,
              isBlocked: false,
              referralCode: ''
            });
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading orders:', error);
        setLoading(false);
        setOrders(dummyOrders);
      }
    };
    
    loadOrders();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    // Try to get timezone information from various sources
    // 1. Use browser's timezone as default
    let timezone;
    
    // 2. Check if we have telegramUser with timezone preference
    if (telegramUser && telegramUser.timezone) {
      timezone = telegramUser.timezone;
    } else {
      // Use browser's timezone as fallback
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    // Format the date with the determined timezone
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone
    }).format(date);
  };

  const handleToggleExpand = (orderId: string) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.background.default,
        minHeight: '100vh',
        padding: theme.spacing(2),
      }}
    >
      {/* User greeting with PageHeader component */}
      <PageHeader 
        username={username} 
        title={PAGE_TITLE}
        photoUrl={telegramUser?.photoUrl}
      />

      {/* Page title
      <Typography
        variant="h1"
        sx={{
          fontSize: '32px',
          fontWeight: 700,
          color: colors.text.primary,
          mb: 3,
        }}
      >
        {PAGE_TITLE}
      </Typography> */}

      {/* Orders list */}
      {orders.length === 0 ? (
        <Paper
          sx={{
            backgroundColor: '#1E1E1E',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: colors.text.secondary }}>
            У вас пока нет заказов
          </Typography>
        </Paper>
      ) : (
        <Box>
          {orders.map((order) => (
            <Paper
              key={order.id}
              sx={{
                backgroundColor: '#1E1E1E',
                borderRadius: '12px',
                overflow: 'hidden',
                mb: 2,
              }}
            >
              {/* Order header - always visible */}
              <Box
                sx={{
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  backgroundColor: expandedOrder === order.id ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  },
                }}
                onClick={() => handleToggleExpand(order.id)}
              >
                <Typography
                  sx={{
                    color: colors.text.primary,
                    fontWeight: 500,
                  }}
                >
                  {order.orderCode}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={
                      order.status === 'pending' ? 'Ожидает' :
                      order.status === 'completed' ? 'Выполнен' :
                      'Отменен'
                    }
                    sx={{
                      backgroundColor: 
                        order.status === 'pending' ? '#FFC107' :
                        order.status === 'completed' ? '#4CAF50' :
                        '#F44336',
                      color: order.status === 'pending' ? '#000' : '#fff',
                      fontWeight: 500,
                      fontSize: '12px',
                      height: '24px',
                      mr: 1,
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{ color: expandedOrder === order.id ? colors.primary.main : colors.text.secondary }}
                  >
                    {expandedOrder === order.id ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                  </IconButton>
                </Box>
              </Box>
              
              {/* Expandable order details */}
              <Collapse in={expandedOrder === order.id} timeout={300}>
                <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                
                <Box 
                  sx={{ 
                    padding: '16px',
                    animation: expandedOrder === order.id ? 'fadeIn 0.3s ease-in-out' : 'none',
                    '@keyframes fadeIn': {
                      '0%': {
                        opacity: 0,
                      },
                      '100%': {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: colors.text.secondary }}>
                        Количество
                      </Typography>
                      <Typography sx={{ color: colors.text.primary, fontWeight: 500 }}>
                        {order.amountUc ? `${order.amountUc} UC` : order.amountBonus ? `${order.amountBonus} бонусов` : 'Н/Д'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: colors.text.secondary }}>
                        Сумма заказа
                      </Typography>
                      <Typography sx={{ color: colors.text.primary, fontWeight: 500 }}>
                        {order.price} рублей
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: colors.text.secondary }}>
                        Дата покупки
                      </Typography>
                      <Typography sx={{ color: colors.text.primary, fontWeight: 500 }}>
                        {formatDate(order.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OrdersHistoryPage;
